// ============================================
// REGISTROACCESOS.JS — la pantalla para leer la camara (SEGURIDAD-ENDPOINTS-ABIERTOS, 2026-09-18)
// ============================================
// "Un registro que nadie puede leer no sirve de nada" (Ricardo). Esto es la ventana.
//
// SOLO DIRECCION. La opcion no se dibuja para nadie mas, y el endpoint responde 403 por su cuenta:
// el candado de verdad es el del servidor (src/routes/seguridadAccesos.js). Un registro de accesos
// dice quien entro, desde donde y a que hora — es informacion sensible por si misma.
//
// LO PRIMERO QUE SE VE al abrir son las IPs con muchos rechazos en poco tiempo. Es la señal que
// importa: un 401 suelto es un despiste; cuarenta seguidos desde la misma direccion, no.

import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, RefreshCw, Filter, X } from 'lucide-react';
import { API_BASE } from './config/api';
import { authHeaders } from './utils/storage';

const FF = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";
const COLOR_OSCURO = '#270f2b';
const COLOR_GRIS = '#5e666e';
const COLOR_GRIS_CL = '#9aa0a6';
const COLOR_BORDE = '#e5e7eb';
const COLOR_AZUL = '#185FA5';

// Cada familia con su color, para reconocerla de un vistazo en la tabla.
const GRUPOS = [
  { id: '',             label: 'Todo' },
  { id: 'dinero',       label: 'Dinero',        color: '#16a34a' },
  { id: 'clientes',     label: 'Clientes',      color: '#2f5f9e' },
  { id: 'identidad',    label: 'Identidad',     color: '#7c3aed' },
  { id: 'irreversible', label: 'Irreversible',  color: '#dc2626' },
  { id: 'publica',      label: 'Publicas',      color: '#d97706' },
  { id: 'rechazo',      label: 'Rechazos',      color: '#b91c1c' },
];
const colorGrupo = (g) => (GRUPOS.find(x => x.id === g) || {}).color || COLOR_GRIS_CL;

const fechaHora = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export default function RegistroAccesos({ esCelular }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState({ grupo: '', ip: '', ruta: '', solo_rechazos: false, desde: '', hasta: '' });

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (filtros.grupo) qs.set('grupo', filtros.grupo);
      if (filtros.ip) qs.set('ip', filtros.ip.trim());
      if (filtros.ruta) qs.set('ruta', filtros.ruta.trim());
      if (filtros.solo_rechazos) qs.set('solo_rechazos', '1');
      if (filtros.desde) qs.set('desde', filtros.desde);
      if (filtros.hasta) qs.set('hasta', filtros.hasta);
      const r = await fetch(`${API_BASE}/api/seguridad/accesos?${qs}`, { headers: authHeaders() });
      const j = await r.json().catch(() => null);
      if (r.status === 403) { setError('Esta pantalla es solo para direccion.'); setDatos(null); return; }
      if (!j || !j.ok) { setError((j && j.error) || `HTTP ${r.status}`); setDatos(null); return; }
      setDatos(j);
    } catch (e) {
      setError(e.message || 'Sin conexion');
    } finally {
      setCargando(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const hayFiltros = !!(filtros.grupo || filtros.ip || filtros.ruta || filtros.solo_rechazos || filtros.desde || filtros.hasta);
  const set = (k, v) => setFiltros(f => ({ ...f, [k]: v }));
  const pad = esCelular ? 16 : 28;

  const campo = {
    minHeight: 40, padding: '0 10px', borderRadius: 8, border: `1px solid ${COLOR_BORDE}`,
    fontFamily: FF, fontSize: 13, color: COLOR_OSCURO, outline: 'none', background: '#fff', boxSizing: 'border-box',
  };
  const th = {
    padding: '9px 10px', textAlign: 'left', fontSize: 10.5, fontWeight: 800, letterSpacing: .5,
    textTransform: 'uppercase', color: COLOR_GRIS_CL, borderBottom: `1px solid ${COLOR_BORDE}`, whiteSpace: 'nowrap',
  };
  const td = { padding: '8px 10px', fontSize: 12.5, color: COLOR_OSCURO, borderBottom: `1px solid #f1f3f5`, verticalAlign: 'top' };

  return (
    <div style={{ fontFamily: FF, color: COLOR_GRIS, background: '#f3f4f4', minHeight: '100%', padding: pad }}>
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ fontFamily: FF, color: COLOR_OSCURO, fontSize: esCelular ? 22 : 28, fontWeight: 700, margin: 0 }}>
          Registro de accesos
        </h1>
        <div style={{ marginTop: 5, fontSize: 13, color: COLOR_GRIS, lineHeight: 1.5, maxWidth: 760 }}>
          Quien ha entrado a las direcciones sensibles, desde donde y con que resultado. Se guardan{' '}
          <strong>{datos?.resumen?.dias_retencion ?? 90} dias</strong> y despues se borran solos.
          No se guardan contraseñas, ni claves, ni el contenido de las peticiones.
        </div>
      </div>

      {/* ---- LO PRIMERO: IPs con muchos rechazos ---- */}
      {datos?.ips_sospechosas?.length > 0 && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12,
          padding: 14, marginBottom: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <ShieldAlert size={18} color="#b91c1c" />
            <strong style={{ color: '#b91c1c', fontSize: 14 }}>
              Direcciones con muchos intentos rechazados
            </strong>
          </div>
          <div style={{ fontSize: 12.5, color: '#7f1d1d', marginBottom: 10, lineHeight: 1.5 }}>
            En las ultimas {datos.resumen?.ventana_horas ?? 24} horas, estas direcciones acumulan{' '}
            {datos.umbral_sospecha} o mas rechazos. Un rechazo suelto es un despiste; muchos seguidos
            desde el mismo sitio, no.
          </div>
          {datos.ips_sospechosas.map(s => (
            <div key={s.ip} style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              padding: '8px 10px', background: '#fff', border: '1px solid #fecaca',
              borderRadius: 9, marginBottom: 6,
            }}>
              <strong style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, color: '#b91c1c' }}>{s.ip}</strong>
              <span style={{ fontSize: 12.5, color: '#7f1d1d' }}>
                <strong>{s.rechazos}</strong> rechazos · {s.rutas_distintas} direcciones distintas
              </span>
              <span style={{ fontSize: 11.5, color: COLOR_GRIS_CL }}>{fechaHora(s.desde)} → {fechaHora(s.hasta)}</span>
              <button
                onClick={() => setFiltros(f => ({ ...f, ip: s.ip, solo_rechazos: false }))}
                style={{
                  marginLeft: 'auto', minHeight: 36, padding: '0 12px', borderRadius: 8,
                  border: '1px solid #fecaca', background: '#fff', color: '#b91c1c',
                  fontFamily: FF, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                }}
              >Ver todo lo de esta IP</button>
            </div>
          ))}
        </div>
      )}

      {/* ---- Resumen de las ultimas 24 h ---- */}
      {datos?.resumen && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          {[
            { k: 'total', label: `accesos en ${datos.resumen.ventana_horas} h`, v: datos.resumen.total, c: COLOR_AZUL },
            { k: 'rechazos', label: 'rechazados (401/403)', v: datos.resumen.rechazos, c: '#b91c1c' },
            { k: 'sin_sesion', label: 'sin sesion iniciada', v: datos.resumen.sin_sesion, c: '#d97706' },
          ].map(x => (
            <div key={x.k} style={{
              flex: '1 1 160px', background: '#fff', border: `1px solid ${COLOR_BORDE}`,
              borderRadius: 12, padding: '10px 14px',
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: x.c }}>{x.v ?? '—'}</div>
              <div style={{ fontSize: 12, color: COLOR_GRIS }}>{x.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ---- Filtros ---- */}
      <div style={{
        background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12,
        padding: 12, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
      }}>
        <Filter size={16} color={COLOR_GRIS_CL} />
        {GRUPOS.map(g => (
          <button key={g.id || 'todo'} onClick={() => set('grupo', g.id)} style={{
            minHeight: 36, padding: '0 11px', borderRadius: 99, cursor: 'pointer', fontFamily: FF,
            fontSize: 12.5, fontWeight: 600,
            background: filtros.grupo === g.id ? (g.color || COLOR_AZUL) : '#fff',
            color: filtros.grupo === g.id ? '#fff' : COLOR_GRIS,
            border: `1px solid ${filtros.grupo === g.id ? (g.color || COLOR_AZUL) : COLOR_BORDE}`,
          }}>{g.label}</button>
        ))}
        <input value={filtros.ip} onChange={e => set('ip', e.target.value)} placeholder="IP exacta" style={{ ...campo, flex: '0 1 150px' }} />
        <input value={filtros.ruta} onChange={e => set('ruta', e.target.value)} placeholder="Direccion contiene..." style={{ ...campo, flex: '1 1 200px' }} />
        <input type="date" value={filtros.desde} onChange={e => set('desde', e.target.value)} style={{ ...campo, flex: '0 1 150px' }} aria-label="Desde" />
        <input type="date" value={filtros.hasta} onChange={e => set('hasta', e.target.value)} style={{ ...campo, flex: '0 1 150px' }} aria-label="Hasta" />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: COLOR_GRIS, cursor: 'pointer' }}>
          <input type="checkbox" checked={filtros.solo_rechazos} onChange={e => set('solo_rechazos', e.target.checked)} />
          Solo rechazados
        </label>
        {hayFiltros && (
          <button onClick={() => setFiltros({ grupo: '', ip: '', ruta: '', solo_rechazos: false, desde: '', hasta: '' })} style={{
            minHeight: 36, padding: '0 10px', borderRadius: 8, border: 'none', background: 'transparent',
            color: COLOR_AZUL, fontFamily: FF, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}><X size={13} /> Limpiar</button>
        )}
        <button onClick={cargar} style={{
          marginLeft: 'auto', minHeight: 36, padding: '0 12px', borderRadius: 8,
          border: `1px solid ${COLOR_BORDE}`, background: '#fff', color: COLOR_GRIS,
          fontFamily: FF, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 5,
        }}><RefreshCw size={13} /> Actualizar</button>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: 16, borderRadius: 10, fontSize: 13 }}>
          {error}
        </div>
      )}

      {!error && cargando && !datos && (
        <div style={{ textAlign: 'center', padding: 40, color: COLOR_GRIS_CL, fontSize: 13 }}>Cargando el registro...</div>
      )}

      {!error && datos && (
        datos.accesos.length === 0 ? (
          <div style={{
            background: '#fff', border: `1px dashed ${COLOR_BORDE}`, borderRadius: 12,
            padding: '44px 20px', textAlign: 'center',
          }}>
            <div style={{ color: COLOR_OSCURO, fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
              {hayFiltros ? 'Nada coincide con esos filtros' : 'Todavia no hay accesos registrados'}
            </div>
            <div style={{ color: COLOR_GRIS_CL, fontSize: 13 }}>
              {hayFiltros ? 'Prueba a quitar algun filtro.' : 'La camara acaba de encenderse: se iran llenando solas.'}
            </div>
          </div>
        ) : (
          <div style={{ background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12, overflow: 'auto', maxHeight: 'calc(var(--beep-vh) * 62)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ background: '#fafafa' }}>
                  <th style={th}>Cuando</th>
                  <th style={th}>Que pidio</th>
                  <th style={th}>Grupo</th>
                  <th style={th}>Quien</th>
                  <th style={th}>Desde</th>
                  <th style={{ ...th, textAlign: 'right' }}>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {datos.accesos.map(a => {
                  const rechazo = a.http_status === 401 || a.http_status === 403;
                  return (
                    <tr key={a.id} style={{ background: rechazo ? '#fff7f7' : '#fff' }}>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: COLOR_GRIS }}>{fechaHora(a.fecha)}</td>
                      <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontSize: 11.5 }}>
                        <strong style={{ color: COLOR_GRIS_CL, marginRight: 6 }}>{a.metodo}</strong>{a.ruta}
                      </td>
                      <td style={td}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, color: colorGrupo(a.grupo),
                          background: colorGrupo(a.grupo) + '18', padding: '2px 7px', borderRadius: 6, whiteSpace: 'nowrap',
                        }}>{a.grupo}</span>
                      </td>
                      <td style={td}>
                        {a.con_sesion
                          ? <span>{a.usuario_nombre || `#${a.usuario_id}`}</span>
                          : <span style={{ color: '#d97706', fontWeight: 700 }}>sin sesion</span>}
                      </td>
                      <td style={{ ...td, fontFamily: 'ui-monospace, monospace', fontSize: 11.5, color: COLOR_GRIS }}>
                        {a.ip || '—'}
                        {a.navegador && (
                          <div title={a.navegador} style={{
                            fontSize: 10.5, color: COLOR_GRIS_CL, maxWidth: 220,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: FF,
                          }}>{a.navegador}</div>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: 800, color: rechazo ? '#b91c1c' : (a.http_status >= 500 ? '#d97706' : '#16a34a') }}>
                          {a.http_status}
                        </span>
                        <span style={{ marginLeft: 6, fontSize: 11, color: COLOR_GRIS_CL }}>{a.duracion_ms} ms</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {datos?.resumen?.mas_antiguo && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: COLOR_GRIS_CL }}>
          Lo mas antiguo que se conserva: {fechaHora(datos.resumen.mas_antiguo)}. Todo lo anterior a{' '}
          {datos.resumen.dias_retencion} dias ya se borro.
        </div>
      )}
    </div>
  );
}
